'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Play, ChevronRight, Sparkles } from 'lucide-react';
import { useBrandLogos } from '@/hooks/useBrandLogos';
import { createClient } from '@/lib/supabase/client';

const stats = [
  { id: 'stat-programs', value: '4,200+', label: 'Programs Created' },
  { id: 'stat-schools', value: '380+', label: 'Schools & Venues' },
  { id: 'stat-views', value: '1.2M+', label: 'Program Views' },
];

// Slugs for the 3 hero cards
const HERO_SLUGS = ['les-miserables', 'hamilton', 'chicago'];

export default function HeroSection() {
  const [currentShow, setCurrentShow] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [coverImages, setCoverImages] = useState<Record<string, string | null>>({});
  const shows = ['Hamilton', 'Les Misérables', 'The Phantom of the Opera', 'Grease', 'Chicago'];
  const { logoIconUrl, logoWordmarkUrl } = useBrandLogos();

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setCurrentShow((prev) => (prev + 1) % shows?.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [shows?.length]);

  useEffect(() => {
    async function fetchCovers() {
      const supabase = createClient();
      const { data } = await supabase
        .from('musicals')
        .select('slug, cover_image_url')
        .in('slug', HERO_SLUGS);
      if (data) {
        const map: Record<string, string | null> = {};
        data.forEach((row: { slug: string; cover_image_url: string | null }) => {
          map[row.slug] = row.cover_image_url;
        });
        setCoverImages(map);
      }
    }
    fetchCovers();
  }, []);

  const lesMisCover = coverImages['les-miserables'];
  const hamiltonCover = coverImages['hamilton'];
  const chicagoCover = coverImages['chicago'];

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden" style={{ background: 'linear-gradient(160deg, #16192b 0%, #1e2235 40%, #1a1d2e 100%)' }}>
      {/* Vivid gradient blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-0 w-[600px] h-[600px] blur-3xl animate-blob-1" style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.3) 0%, transparent 65%)' }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] blur-3xl animate-blob-2" style={{ background: 'radial-gradient(circle, rgba(236,72,153,0.22) 0%, transparent 65%)' }} />
        <div className="absolute top-1/2 right-1/4 w-80 h-80 blur-3xl animate-blob-3" style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.18) 0%, transparent 65%)' }} />
        <div className="absolute bottom-1/4 left-1/3 w-64 h-64 blur-3xl animate-blob-4" style={{ background: 'radial-gradient(circle, rgba(14,165,233,0.15) 0%, transparent 65%)' }} />
        {/* Diagonal accent stripe */}
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.3) 0px, rgba(255,255,255,0.3) 1px, transparent 1px, transparent 60px)' }} />
      </div>
      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.8) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />
      <div className="relative max-w-screen-2xl mx-auto px-6 lg:px-10 py-20 text-center">
        {/* Badge */}
        <div
          className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-8 ${mounted ? 'reveal-fade delay-0' : 'reveal-hidden'}`}
          style={{ background: 'linear-gradient(135deg, rgba(139,92,246,0.2), rgba(236,72,153,0.15))', border: '1px solid rgba(139,92,246,0.35)' }}
        >
          <Sparkles size={12} className="text-violet-400" />
          <span className="text-xs font-bold text-violet-300 tracking-wide">Trusted by 380+ Schools & Theatres</span>
        </div>

        {/* Headline */}
        <h1 className={`text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-extrabold leading-tight mb-6 tracking-tight ${mounted ? 'reveal-up delay-100' : 'reveal-hidden'}`}>
          <span className="text-zinc-100">Beautiful Programs</span>
          <br />
          <span className="text-gradient-gold">for Every Stage</span>
        </h1>

        {/* Rotating show name */}
        <div className={`flex items-center justify-center gap-3 mb-6 ${mounted ? 'reveal-up delay-200' : 'reveal-hidden'}`}>
          <span className="text-zinc-500 text-lg font-medium">Currently building:</span>
          <span
            className="font-bold text-lg tabular-nums transition-all duration-500 px-3 py-0.5 rounded-md"
            style={{ background: 'rgba(245,158,11,0.12)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.25)' }}
          >
            {shows?.[currentShow]}
          </span>
        </div>

        <p className={`text-zinc-400 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed ${mounted ? 'reveal-up delay-300' : 'reveal-hidden'}`}>
          Create stunning digital Playbill-style programs for your production in minutes.
          Free to create, beautiful to share, 25% cheaper to print.
        </p>

        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mb-16 ${mounted ? 'reveal-up delay-400' : 'reveal-hidden'}`}>
          <Link
            href="/sign-up-login-screen"
            className="btn-gold group flex items-center gap-2 font-bold px-8 py-4 rounded-xl text-base"
            style={{ background: 'linear-gradient(135deg, #f59e0b, #f97316)', color: '#0f0f1a', boxShadow: '0 0 32px rgba(245,158,11,0.4)' }}
          >
            Start Your Program Free
            <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            href="/program-viewer"
            className="btn-ghost flex items-center gap-2 font-semibold px-8 py-4 rounded-xl text-base text-zinc-200 hover:text-white"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)' }}
          >
            <Play size={16} className="text-pink-400" />
            See a Live Program
          </Link>
        </div>

        {/* Stats */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-16 ${mounted ? 'reveal-fade delay-500' : 'reveal-hidden'}`}>
          {stats?.map((stat, i) => {
            const colors = ['text-gradient-gold', 'text-gradient-violet', 'text-gradient-pink'];
            return (
              <div key={stat?.id} className="text-center">
                <div className={`text-3xl font-extrabold tabular-nums ${colors?.[i]}`}>{stat?.value}</div>
                <div className="text-sm text-zinc-500 font-medium mt-1">{stat?.label}</div>
              </div>
            );
          })}
        </div>

        {/* Floating program preview cards — actual program cover format */}
        <div className={`mt-20 relative max-w-4xl mx-auto hidden lg:block ${mounted ? 'reveal-scale delay-600' : 'reveal-hidden'}`}>
          {/* Left card — Les Misérables */}
          <div
            className="absolute -left-4 top-2 shadow-2xl rotate-[-10deg] opacity-75 overflow-hidden transition-all duration-300 ease-out hover:opacity-100 hover:scale-105 hover:rotate-[-6deg] hover:shadow-[0_20px_60px_rgba(139,92,246,0.4)] hover:z-10 cursor-pointer"
            style={{
              width: '138px',
              height: '210px',
              borderRadius: '4px',
              border: '1px solid rgba(139,92,246,0.35)',
            }}
          >
            {/* 20% black header */}
            <div
              className="flex items-center justify-center gap-1"
              style={{ height: '42px', background: '#000', borderBottom: '1px solid rgba(139,92,246,0.4)' }}
            >
              {logoWordmarkUrl ? (
                <img src={logoWordmarkUrl} alt="Ovation" className="object-contain" style={{ height: '14.4px', filter: 'brightness(0) saturate(100%) invert(60%) sepia(60%) saturate(400%) hue-rotate(220deg)' }} />
              ) : (
                <span className="text-[8px] font-bold tracking-widest uppercase text-violet-300">Ovation</span>
              )}
            </div>
            {/* 80% cover content */}
            {lesMisCover ? (
              <div style={{ height: '168px', overflow: 'hidden' }}>
                <img src={lesMisCover} alt="Les Misérables cover" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-between p-2"
                style={{ height: '168px', background: 'linear-gradient(160deg, #1a0a2e 0%, #2d1b4e 50%, #1a0a2e 100%)' }}
              >
                <div className="text-center mt-1">
                  <div className="text-[6px] text-violet-300 tracking-widest uppercase mb-1">Riverside Theatre</div>
                  <div className="text-[11px] font-extrabold text-white leading-tight">Les<br />Misérables</div>
                  <div className="mt-1 w-10 h-px mx-auto" style={{ background: 'rgba(139,92,246,0.6)' }} />
                </div>
                <div className="w-full flex items-end justify-center" style={{ height: '80px' }}>
                  <div className="relative w-full h-full flex items-end justify-center">
                    <div className="absolute bottom-0 left-0 right-0 h-8" style={{ background: 'rgba(139,92,246,0.12)', borderRadius: '2px 2px 0 0' }} />
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-1 h-10" style={{ background: 'rgba(139,92,246,0.3)' }} />
                    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-8 h-px" style={{ background: 'rgba(139,92,246,0.3)', top: '32px' }} />
                    <div className="absolute" style={{ bottom: '18px', left: '50%', transform: 'translateX(-50%)' }}>
                      <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(139,92,246,0.5)' }} />
                    </div>
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[6px] text-violet-400 tracking-wide">Spring 2026</div>
                </div>
              </div>
            )}
          </div>

          {/* Right card — Chicago */}
          <div
            className="absolute -right-4 top-6 shadow-2xl rotate-[10deg] opacity-75 overflow-hidden transition-all duration-300 ease-out hover:opacity-100 hover:scale-105 hover:rotate-[6deg] hover:shadow-[0_20px_60px_rgba(236,72,153,0.4)] hover:z-10 cursor-pointer"
            style={{
              width: '138px',
              height: '210px',
              borderRadius: '4px',
              border: '1px solid rgba(236,72,153,0.35)',
            }}
          >
            {/* 20% black header */}
            <div
              className="flex items-center justify-center gap-1"
              style={{ height: '42px', background: '#000', borderBottom: '1px solid rgba(236,72,153,0.4)' }}
            >
              {logoWordmarkUrl ? (
                <img src={logoWordmarkUrl} alt="Ovation" className="object-contain" style={{ height: '14.4px', filter: 'brightness(0) saturate(100%) invert(70%) sepia(60%) saturate(400%) hue-rotate(290deg)' }} />
              ) : (
                <span className="text-[8px] font-bold tracking-widest uppercase text-pink-300">Ovation</span>
              )}
            </div>
            {/* 80% cover content */}
            {chicagoCover ? (
              <div style={{ height: '168px', overflow: 'hidden' }}>
                <img src={chicagoCover} alt="Chicago cover" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-between p-2"
                style={{ height: '168px', background: 'linear-gradient(160deg, #1a0010 0%, #3d0020 50%, #1a0010 100%)' }}
              >
                <div className="text-center mt-1">
                  <div className="text-[6px] text-pink-300 tracking-widest uppercase mb-1">Lakewood Arts</div>
                  <div className="text-[14px] font-extrabold text-white leading-tight tracking-wider">CHICAGO</div>
                  <div className="mt-1 w-10 h-px mx-auto" style={{ background: 'rgba(236,72,153,0.6)' }} />
                </div>
                <div className="w-full flex flex-col items-center justify-center" style={{ height: '80px' }}>
                  <div className="flex gap-1 items-end mb-1">
                    {[18, 28, 36, 28, 18]?.map((h, i) => (
                      <div key={i} style={{ width: '8px', height: `${h}px`, background: i === 2 ? 'rgba(236,72,153,0.7)' : 'rgba(236,72,153,0.3)', borderRadius: '1px 1px 0 0' }} />
                    ))}
                  </div>
                  <div className="w-16 h-px" style={{ background: 'rgba(236,72,153,0.5)' }} />
                  <div className="text-[7px] text-pink-400 mt-1 tracking-widest">THE MUSICAL</div>
                </div>
                <div className="text-center">
                  <div className="text-[6px] text-pink-400 tracking-wide">Fall 2025</div>
                </div>
              </div>
            )}
          </div>

          {/* Center main card — Hamilton */}
          <div
            className="shadow-2xl mx-auto page-turn-shadow overflow-hidden transition-all duration-300 ease-out hover:scale-[1.04] hover:shadow-[0_24px_70px_rgba(245,158,11,0.45)] cursor-pointer"
            style={{
              width: '184px',
              height: '280px',
              borderRadius: '6px',
              border: '1px solid rgba(245,158,11,0.3)',
            }}
          >
            {/* 20% black header */}
            <div
              className="flex items-center justify-center gap-1.5"
              style={{ height: '56px', background: '#000', borderBottom: '1px solid rgba(245,158,11,0.4)' }}
            >
              {logoWordmarkUrl ? (
                <img src={logoWordmarkUrl} alt="Ovation" className="object-contain" style={{ height: '19.2px', filter: 'brightness(0) saturate(100%) invert(75%) sepia(80%) saturate(500%) hue-rotate(5deg)' }} />
              ) : (
                <span className="text-[9px] font-bold tracking-widest uppercase text-amber-400">Ovation</span>
              )}
            </div>
            {/* 80% cover content */}
            {hamiltonCover ? (
              <div style={{ height: '224px', overflow: 'hidden' }}>
                <img src={hamiltonCover} alt="Hamilton cover" className="w-full h-full object-cover" />
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-between px-3 py-3"
                style={{ height: '224px', background: 'linear-gradient(160deg, #0a0a0f 0%, #1a1508 50%, #0a0a0f 100%)' }}
              >
                <div className="text-center">
                  <div className="text-[7px] text-amber-400 tracking-widest uppercase mb-1.5">Westside High Presents</div>
                  <div
                    className="font-extrabold text-white leading-none"
                    style={{ fontSize: '22px', fontFamily: 'Georgia, serif', letterSpacing: '0.05em' }}
                  >
                    Hamilton
                  </div>
                  <div className="mt-2 flex items-center gap-1 justify-center">
                    <div className="h-px flex-1" style={{ background: 'rgba(245,158,11,0.5)' }} />
                    <div className="w-1 h-1 rounded-full" style={{ background: '#f59e0b' }} />
                    <div className="h-px flex-1" style={{ background: 'rgba(245,158,11,0.5)' }} />
                  </div>
                </div>
                {/* Silhouette / graphic area */}
                <div className="w-full flex flex-col items-center justify-center" style={{ height: '110px' }}>
                  <div className="relative flex items-end justify-center w-full" style={{ height: '90px' }}>
                    <div className="absolute bottom-0 left-0 right-0 h-6" style={{ background: 'rgba(245,158,11,0.08)', borderRadius: '2px 2px 0 0' }} />
                    <div className="relative flex flex-col items-center" style={{ marginBottom: '6px' }}>
                      <div className="w-4 h-4 rounded-full" style={{ background: 'rgba(245,158,11,0.35)', marginBottom: '2px' }} />
                      <div className="w-6 h-10" style={{ background: 'rgba(245,158,11,0.25)', borderRadius: '2px 2px 0 0', clipPath: 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)' }} />
                    </div>
                    {['-28px', '28px']?.map((x, i) => (
                      <div key={i} className="absolute" style={{ bottom: '30px', left: `calc(50% + ${x})`, opacity: 0.5 }}>
                        <div className="w-1 h-1 rounded-full" style={{ background: '#f59e0b' }} />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-[7px] text-amber-500 tracking-widest uppercase">Spring 2026</div>
                  <div className="mt-1 px-3 py-0.5 text-[6px] font-bold tracking-widest" style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)', borderRadius: '2px' }}>DIGITAL PROGRAM</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}